import React from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import {
  AppContainer,
  MainContentContainer,
  BodyContainer,
} from '../../resources/styling/appStyle';
import { SideMenu } from '../../components/layouts/SideMenu';
import { Header } from '../../components/layouts/Header';
import '../../resources/styling/App.scss';
import {
  sa,
  PageTitle,
  SectionHeaderRow,
  Card,
  SectionStates,
  PillButton,
} from '../../components/superAdminUi';
import emailTemplateVariantService from '../../services/api/emailTemplateVariantService';
import EmailTemplateEditor from './EmailTemplateEditor';

// WI-443: Super-Admin management of customisable system emails. One card per
// category (activation, welcome, password reset, survey launched, closing
// reminder); each shows the default template plus criteria-scoped variants.

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sa.space.section};
  width: 100%;
`;

const VariantRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 0;
  border-top: 1px solid ${sa.color.stroke};
  font-family: ${sa.font.family};
  opacity: ${(p) => (p.$inactive ? 0.55 : 1)};
`;

const VariantName = styled.span`
  font-weight: ${sa.font.bold};
  font-size: 13px;
  color: ${sa.color.text};
`;

const VariantSubject = styled.span`
  font-size: 12px;
  color: #777;
  flex: 1 1 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chip = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: ${sa.font.bold};
  padding: 2px 10px;
  border-radius: 999px;
  background: ${(p) => (p.$tone === 'default' ? sa.color.teal : p.$tone === 'inactive' ? '#9e9e9e' : '#eef4f4')};
  color: ${(p) => (p.$tone === 'default' || p.$tone === 'inactive' ? sa.color.white : sa.color.text)};
  border: ${(p) => (p.$tone === 'criteria' ? `1px solid ${sa.color.stroke}` : 'none')};
`;

const Actions = styled.span`
  display: inline-flex;
  gap: 8px;
  margin-left: auto;
`;

export function criteriaChips(variant, products) {
  const chips = [];
  const productName = (id) => {
    const found = (products || []).find((p) => p.Id === id);
    return found ? found.Name : `Product ${id}`;
  };
  (variant.Products || []).forEach((id) =>
    chips.push(`Product: ${productName(id)}`)
  );
  (variant.OrganisationTypes || []).forEach((t) => chips.push(`Type: ${t}`));
  (variant.GroupTypes || []).forEach((t) => chips.push(`Group type: ${t}`));
  (variant.Countries || []).forEach((c) => chips.push(`Country: ${c}`));
  return chips;
}

export default function EmailTemplates() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [options, setOptions] = React.useState(null);
  const [variants, setVariants] = React.useState([]);
  const [editor, setEditor] = React.useState(null); // { category, variant | null }

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [optionsRes, variantsRes] = await Promise.all([
        emailTemplateVariantService.getOptions(),
        emailTemplateVariantService.getAllVariants(),
      ]);
      if (optionsRes.success === false) throw new Error(optionsRes.message);
      if (variantsRes.success === false) throw new Error(variantsRes.message);

      let allVariants = variantsRes.data || [];
      if (allVariants.length === 0) {
        // First run: create the default templates, then refetch (idempotent).
        const seeded = await emailTemplateVariantService.seedDefaults();
        if (seeded.success !== false) {
          const refetch = await emailTemplateVariantService.getAllVariants();
          allVariants = refetch.data || [];
        }
      }
      setOptions(optionsRes.data);
      setVariants(allVariants);
    } catch (e) {
      setError(e.message || 'Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const onDelete = async (variant) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${variant.Name}"? This cannot be undone.`)) return;
    const res = await emailTemplateVariantService.deleteVariant(variant.Id);
    if (res.success === false) {
      toast.error(res.message || 'Delete failed');
      return;
    }
    toast.success('Template deleted');
    load();
  };

  const onClone = async (variant, category) => {
    const res = await emailTemplateVariantService.cloneVariant(variant.Id);
    if (res.success === false) {
      toast.error(res.message || 'Clone failed');
      return;
    }
    toast.success('Template cloned — set its criteria and activate it');
    await load();
    setEditor({ category, variant: res.data });
  };

  const categories = (options && options.Categories) || [];
  const products = (options && options.Products) || [];

  return (
    <AppContainer>
      <SideMenu />
      <BodyContainer>
        <Header />
        <MainContentContainer>
          <Stack>
            <SectionHeaderRow
              title={<PageTitle>Email Templates</PageTitle>}
              subtitle='Customise system emails by product, customer type, group type and country. The default is used when no other criteria apply.'
            />
            <SectionStates
              loading={loading}
              error={error}
              isEmpty={!loading && !error && categories.length === 0}
              emptyText='No email template categories available.'
              onRetry={load}
            />
            {!loading &&
              !error &&
              categories.map((category) => {
                const rows = variants.filter(
                  (v) => v.TemplateKey === category.TemplateKey
                );
                return (
                  <Card key={category.TemplateKey}>
                    <SectionHeaderRow title={category.Label} size='sm'>
                      <PillButton
                        type='button'
                        onClick={() => setEditor({ category, variant: null })}
                      >
                        Add variant
                      </PillButton>
                    </SectionHeaderRow>
                    {rows.length === 0 && (
                      <VariantRow>
                        <VariantSubject>No templates yet.</VariantSubject>
                      </VariantRow>
                    )}
                    {rows.map((variant) => (
                      <VariantRow key={variant.Id} $inactive={!variant.IsActive}>
                        <VariantName>{variant.Name}</VariantName>
                        {variant.IsDefault && <Chip $tone='default'>Default</Chip>}
                        {!variant.IsActive && <Chip $tone='inactive'>Inactive</Chip>}
                        {criteriaChips(variant, products).map((label) => (
                          <Chip key={label} $tone='criteria'>
                            {label}
                          </Chip>
                        ))}
                        <VariantSubject title={variant.Subject}>
                          {variant.Subject}
                        </VariantSubject>
                        <Actions>
                          <PillButton
                            type='button'
                            $variant='outline'
                            onClick={() => setEditor({ category, variant })}
                          >
                            Edit
                          </PillButton>
                          <PillButton
                            type='button'
                            $variant='outline'
                            onClick={() => onClone(variant, category)}
                          >
                            Clone
                          </PillButton>
                          {!variant.IsDefault && (
                            <PillButton
                              type='button'
                              $variant='danger'
                              onClick={() => onDelete(variant)}
                            >
                              Delete
                            </PillButton>
                          )}
                        </Actions>
                      </VariantRow>
                    ))}
                  </Card>
                );
              })}
          </Stack>
          {editor && (
            <EmailTemplateEditor
              category={editor.category}
              variant={editor.variant}
              options={options}
              onClose={() => setEditor(null)}
              onSaved={() => {
                setEditor(null);
                load();
              }}
            />
          )}
        </MainContentContainer>
      </BodyContainer>
    </AppContainer>
  );
}
